import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initialGeospoofState } from 'constants/geospoof';
import {
  initialFeatureFiltersState,
  initialMapAttributesState,
} from 'constants/map';
import { reducersNames } from 'constants/reducers';
import {
  IDeleteMode,
  IErrorHandler,
  IGeospoofEntity,
  IMapMediaPreview,
  IMapState,
  IPicturesUploadStatus,
  IUserLocationError,
} from 'interfaces';
import { TGeospoofObjectKeysUnion, TGeospoofTabsUnion, TPosition } from 'types';

import { geospoofErrorHandler, getMediaFiles, isVideo, notify } from 'utils';

import {
  getAttributesThunk,
  getEntityPicturesThunk,
  getGeospoofThunk,
  getLoadedUserLocationThunk,
  getPicturesUploadIdThunk,
  getSearchHistoryThunk,
  getSearchInfoThunk,
  getUserLocationThunk,
} from './actions';

const initialState: IMapState = {
  isModified: false,
  geospoof: initialGeospoofState,
  filters: initialFeatureFiltersState,
  deleteMode: null,
  mediaPreviewMode: null,
  measurements: {
    currentMeasureControl: '',
    measureResult: null,
  },
  attributes: initialMapAttributesState,

  openedPopup: null,
  currentEntity: null,
};

const mapSlice = createSlice({
  name: reducersNames.MAP,
  initialState,
  reducers: {
    // Geospoof actions
    setGeospoofStatus(state, action) {
      state.geospoof.isActive = action.payload;
    },
    setGeospoofCoordinates(state, action) {
      state.geospoof.searchPosition = action.payload;
    },
    setExternalGeospoofRequest(state, action: PayloadAction<TPosition | null>) {
      if (action.payload) {
        state.geospoof.searchPosition = action.payload;
        state.geospoof.runExternalRequest = true;
      } else {
        state.geospoof.runExternalRequest = false;
      }
    },
    setObjectCoordinates(state) {
      state.geospoof.objectCoordinates = state.geospoof.searchPosition;
    },
    setCurrentGeospoofObject(
      state,
      action: PayloadAction<{
        field: TGeospoofTabsUnion;
        data: IGeospoofEntity | null;
      }>
    ) {
      state.geospoof.current[action.payload.field] = action.payload.data;
      state.mediaPreviewMode = null;
    },
    setVisibleGeospoofObjects(state, action) {
      state.geospoof.visibleObjects = action.payload;
    },
    resetCurrentObject(state) {
      state.geospoof.current.groups = null;
      state.geospoof.current.users = null;
      state.geospoof.current.history = null;
    },
    resetGeospoofState(state) {
      state.geospoof = initialGeospoofState;
      state.filters = initialFeatureFiltersState;
    },
    setPicturesUploadStatus(
      state,
      action: PayloadAction<IPicturesUploadStatus>
    ) {
      const { field, id, value } = action.payload;

      state.geospoof.objects[field] = state.geospoof.objects[field].map(
        (entity) => {
          if (entity.id === id) {
            return {
              ...entity,
              isMediaLoading: value,
            };
          }
          return entity;
        }
      );
    },
    setGeospoofTab(state, action: PayloadAction<TGeospoofObjectKeysUnion>) {
      state.geospoof.currentTab = action.payload;
    },
    setLoadingUserLocation(state, action) {
      state.geospoof.loadingUserLocation = {
        ...state.geospoof.loadingUserLocation,
        [action.payload]: true,
      };
    },
    // DeleteMode actions
    setDeleteMode(state, action: PayloadAction<IDeleteMode | null>) {
      state.deleteMode = action.payload;
    },
    // Media Preview actions
    setMediaPreviewMode(state, action: PayloadAction<IMapMediaPreview | null>) {
      state.mediaPreviewMode = action.payload;
    },
    // Measurements actions
    setCurrentEntity(state, action) {
      state.currentEntity = action.payload;
    },
    setOpenedPopup(state, action) {
      state.openedPopup = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Geospoof thunks
      .addCase(getGeospoofThunk.fulfilled, (state, action) => {
        state.geospoof.pending = false;
        state.geospoof.objects = {
          ...state.geospoof.objects,
          ...action.payload,
        };

        state.geospoof.objectCoordinates = state.geospoof.searchPosition;
      })
      .addCase(getGeospoofThunk.pending, (state) => {
        state.geospoof.pending = true;
        state.geospoof.current = {
          users: null,
          groups: null,
          history: null,
        };
        state.geospoof.objectCoordinates = null;
        state.mediaPreviewMode = null;
      })
      .addCase(getGeospoofThunk.rejected, (state, action) => {
        state.geospoof.pending = false;
        state.geospoof = {
          ...initialGeospoofState,
          current: state.geospoof.current,
          objects: state.geospoof.objects,
          visibleObjects: state.geospoof.visibleObjects,
        };
        geospoofErrorHandler(action.payload as IErrorHandler);
      })
      .addCase(getPicturesUploadIdThunk.rejected, (state, action) => {
        notify.error(action.payload as string);
      })
      .addCase(getEntityPicturesThunk.pending, (state) => {
        state.mediaPreviewMode = {
          list: [],
          mode: 'geospoof',
          isLoading: true,
        };
      })
      .addCase(getEntityPicturesThunk.fulfilled, (state, action) => {
        if (action.payload) {
          const { links, field, id } = action.payload;

          state.geospoof.objects[field] = state.geospoof.objects[field].map(
            (entity) => {
              if (entity.id === id) {
                let mediaArray = links;
                let image = entity.thumbnail;

                if (!image && mediaArray.length) {
                  image = mediaArray.find((link) => !isVideo(link)) || null;
                }

                if (!mediaArray.length && image) {
                  mediaArray = [image];
                }

                return {
                  ...entity,
                  mediaArray,
                  thumbnail: image,
                  isMediaLoading: false,
                };
              }
              return entity;
            }
          );

          const currentObject = state.geospoof.current[field];

          if (currentObject?.id === id && links.length) {
            state.mediaPreviewMode = {
              list: getMediaFiles(links),
              mode: 'geospoof',
            };

            if (!currentObject.thumbnail && links.length) {
              const image = links.find((link) => !isVideo(link));
              state.geospoof.current[field] = {
                ...currentObject,
                thumbnail: image,
              };
            }
          }
        }
      })
      .addCase(getEntityPicturesThunk.rejected, (state, action) => {
        notify.error(action.payload as string);
      })
      .addCase(getSearchHistoryThunk.fulfilled, (state, action) => {
        state.geospoof.objects = {
          ...state.geospoof.objects,
          history: action.payload.search_history,
          userLocationHistory: action.payload.user_location_history,
        };

        state.geospoof.isHistoryLoading = false;
      })
      .addCase(getSearchHistoryThunk.pending, (state) => {
        state.geospoof.isHistoryLoading = true;
      })
      .addCase(getSearchHistoryThunk.rejected, (state) => {
        state.geospoof.isHistoryLoading = false;
      })
      .addCase(getSearchInfoThunk.fulfilled, (state, action) => {
        if (action.payload) {
          state.geospoof.objects = {
            ...state.geospoof.objects,
            ...action.payload.objects,
          };
          state.geospoof.isSearchInfoLoading = action.payload.loading;
          state.geospoof.objectCoordinates = state.geospoof.searchPosition;
        }
      })
      .addCase(getSearchInfoThunk.pending, (state) => {
        state.geospoof.isSearchInfoLoading = true;
      })
      .addCase(getSearchInfoThunk.rejected, (state, action) => {
        state.geospoof.isSearchInfoLoading = false;

        if (state.geospoof.currentTab === 'history') {
          state.geospoof.objects = {
            ...state.geospoof.objects,
            groups: [],
            users: [],
          };
          delete state.geospoof.objects.searchId;
        }

        notify.error(action.payload as string);
      })
      .addCase(getLoadedUserLocationThunk.fulfilled, (state, action) => {
        const loadedId = action.payload?.user.id;
        const cordinates = action.payload?.location;

        if (cordinates && loadedId) {
          for (const key in state.geospoof.current) {
            const current =
              state.geospoof.current[key as TGeospoofObjectKeysUnion];

            if (current && current?.id === loadedId) {
              state.geospoof.current[key as TGeospoofObjectKeysUnion] = {
                ...current,
                coordinates: [cordinates.lon, cordinates.lat] as TPosition,
              };
            }
          }

          if (
            state.geospoof.objects.users.some((item) => item.id === loadedId)
          ) {
            state.geospoof.objects.users = state.geospoof.objects.users.map(
              (item) => {
                if (item.id === loadedId) {
                  return {
                    ...item,
                    coordinates: [cordinates.lon, cordinates.lat] as TPosition,
                  };
                }
                return item;
              }
            );
          }

          if (
            state.geospoof.objects.userLocationHistory.some(
              (item) => item.requested_user.id === loadedId
            )
          ) {
            state.geospoof.objects.userLocationHistory =
              state.geospoof.objects.userLocationHistory.map((item) => {
                if (item.requested_user.id === loadedId) {
                  return {
                    ...item,
                    coordinates: [cordinates.lon, cordinates.lat] as TPosition,
                  };
                }
                return item;
              });
          }

          state.geospoof.loadingUserLocation[loadedId] = false;
        }
      })
      .addCase(getUserLocationThunk.rejected, (state, action) => {
        const { error, id, message } = action.payload as IUserLocationError &
          Pick<IErrorHandler, 'error'>;

        state.geospoof.loadingUserLocation[id] = false;

        geospoofErrorHandler({ error, message });
      })
      .addCase(getLoadedUserLocationThunk.rejected, (state, action) => {
        const error = action.payload as IUserLocationError;

        state.geospoof.loadingUserLocation[error.id] = false;

        if (
          state.geospoof.objects.userLocationHistory.some(
            (item) => item.requested_user.id === error.id
          )
        ) {
          state.geospoof.objects.userLocationHistory =
            state.geospoof.objects.userLocationHistory.map((item) => {
              if (item.requested_user.id === error.id) {
                return { ...item, request_failed: false };
              }
              return item;
            });
        }

        notify.error(error.message);
      })
      // Attributes thunks
      .addCase(getAttributesThunk.fulfilled, (state, action) => {
        state.attributes = action.payload;
      })
      .addCase(getAttributesThunk.rejected, (state, action) => {
        notify.error(action.payload as string);
      });
  },
});

export const { actions: mapActions, reducer: mapReducer } = mapSlice;
